/**
 * Reusable configuration sections composed by the focused Endpoints,
 * Admin, and My Account pages. The default export is retained only as a
 * compatibility composition for tests and older imports; App routing no
 * longer exposes one mixed-concern Settings page.
 */
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { restGet, restPost, restDelete, authedFetch } from "../lib/restClient.js";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import {
    ComplianceIntegrationHealthProvider,
    useComplianceIntegrationHealth,
} from "../components/ComplianceIntegrationHealth.jsx";
import Select from "../components/Select.jsx";
import AccessSummary from "../components/AccessSummary.jsx";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialCopy, denialOrRawMessage } from "../lib/denialReason.js";
import {
    CHAT_DATA_CLEAR_COPY,
    clearChatData,
    clearWorkflowData,
    clearMcpCredentials,
} from "../lib/clearUserData.js";
import { ACTION_TEXT_CLASS, EYEBROW_CLASS, NOTE_CLASS } from "../lib/ui.js";
import {
    createPerplexityAuditIntegration,
    deletePerplexityAuditIntegration,
    getPerplexityAuditIntegration,
    rotatePerplexityAuditCredential,
} from "../lib/perplexityAuditClient.js";
import {
    createLangSmithIntegration,
    deleteLangSmithIntegration,
    listLangSmithIntegrations,
    listLangSmithProjects,
    setLangSmithWatchedProjects,
} from "../lib/langsmithClient.js";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";
import { runQueryRows } from "../lib/analyticsClient.js";
import {
    DOMAIN_PATTERN_VARIANTS,
    MAX_TENANT_RULES_PER_TENANT,
    hasAnyValidationError,
    newDomainPattern,
    newPathPattern,
    readDomainPatternEntry,
    validateCustomProviderRules,
} from "../lib/customProviders.js";
import {
    DEFAULT_OTEL_COLLECTOR_PORT,
    MAX_OTEL_COLLECTOR_PORT,
    MIN_OTEL_COLLECTOR_PORT,
    parseOtelCollectorPortInput,
    readOtelCollectorPort,
} from "../lib/otelCollectorPort.js";
import {
    getAuthIdentityEpoch,
    getUserMetadata,
    onAuthIdentityChange,
} from "../lib/authTokenStore.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import { langsmithIntegration, perplexitySummary, whenLabel } from "./dataSources/model.js";
import {
    complianceHealthPresentation,
    formatComplianceHealthTime,
} from "../lib/complianceIntegrationHealth.js";

// ── Tenant configuration save (shared, non-clobbering) ───────────
//
// The grpc-gateway treats PUT /v1/tenant/configuration as a FULL REPLACE
// of the `configuration` object. Two consequences drive this helper:
//
//   1. Field names MUST be snake_case. The protojson unmarshaler accepts
//      both snake_case and camelCase, but the dashboard convention is to
//      send snake_case to match the proto definitions.
//   2. Every known field must be present on every save or it's wiped.
//      So a section that only knows about the tray toggle cannot PUT just
//      that - it would erase custom_provider_rules etc.
//
// We therefore re-read the live config, rebuild the full known field set
// (defensively reading either casing the GET emits), overlay the caller's
// change, and PUT the complete snake_case payload. `llm_provider` is
// deliberately omitted: the GET never returns the write-only api_key, so
// round-tripping it would clear the key — it's saved on its own path.
function normalizeRules(raw) {
    return (Array.isArray(raw) ? raw : []).map((r) => ({
        id: r.id ?? "",
        provider_tag: r.providerTag ?? r.provider_tag ?? "",
        domain_patterns: r.domainPatterns ?? r.domain_patterns ?? [],
        path_patterns: r.pathPatterns ?? r.path_patterns ?? [],
        method: r.method ?? "CUSTOM_PROVIDER_HTTP_METHOD_UNSPECIFIED",
        strategy: r.strategy ?? "CUSTOM_CAPTURE_STRATEGY_FULL_BODY",
    }));
}

// Exported for the carry-through test: the full-replace PUT is where an
// unrelated save can silently clear a tenant flag this page has no control for.
export async function saveTenantConfiguration(overlay) {
    const current = await restGet("/v1/tenant/configuration");
    const cfg = current?.configuration ?? {};
    const merged = {
        hide_tray_icon: cfg.hideTrayIcon ?? cfg.hide_tray_icon ?? false,
        // Carried on every save (full-replace PUT): a rules-only save must not
        // silently reset the tenant's collector port to the default (O-1709).
        otel_collector_port: readOtelCollectorPort(cfg),
        custom_provider_rules: normalizeRules(
            cfg.customProviderRules ?? cfg.custom_provider_rules,
        ),
        // Caller overrides only the field(s) it manages (snake_case).
        ...overlay,
    };
    // "Unset" (use the default port) travels as an absent field, not an
    // explicit null — sidesteps protojson null-handling for the optional scalar.
    if (merged.otel_collector_port == null) {
        delete merged.otel_collector_port;
    }
    const res = await authedFetch("/v1/tenant/configuration", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ configuration: merged }),
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
            `PUT /v1/tenant/configuration ${res.status}: ${text || res.statusText}`,
        );
    }
    return res.json().catch(() => ({}));
}

// ── Reusable section frame ───────────────────────────────────────

function useLatestRef(value) {
    const ref = useRef(value);
    ref.current = value;
    return ref;
}

function SectionCard({ id, eyebrow, title, description, footer, children, collapsible = false, defaultCollapsed = false, embedded = false }) {
    const [collapsed, setCollapsed] = useState(collapsible ? defaultCollapsed : false);
    const bodyId = id ? `${id}-body` : undefined;
    const headerInner = (
        <>
            {eyebrow ? (
                <span className={`${EYEBROW_CLASS} text-[color:var(--muted)]`}>{eyebrow}</span>
            ) : null}
            <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em] text-[color:var(--ink)]">
                {title}
            </h2>
            {description ? (
                <p className={`${NOTE_CLASS} text-[12px] leading-[1.4] mt-[4px]`}>
                    {description}
                </p>
            ) : null}
        </>
    );
    if (embedded) {
        return (
            <section id={id} className="grid gap-[12px] border-t border-[color:var(--line)] pt-[14px] first:border-t-0 first:pt-0">
                <header className="grid gap-[2px]">
                    {eyebrow ? (
                        <span className={`${EYEBROW_CLASS} text-[color:var(--muted)]`}>{eyebrow}</span>
                    ) : null}
                    <h3 className="m-0 text-[13px] leading-[1.1] font-medium tracking-[-0.02em] text-[color:var(--ink)]">
                        {title}
                    </h3>
                    {description ? (
                        <p className={`${NOTE_CLASS} text-[11px] leading-[1.4] mt-[3px]`}>
                            {description}
                        </p>
                    ) : null}
                </header>
                <div className="grid gap-[18px]">{children}</div>
                {footer ? <div className="grid">{footer}</div> : null}
            </section>
        );
    }

    // Body stays mounted when collapsed (visibility toggles via a class, not
    // an unmount) so its data still loads in the background and its aria-busy
    // loading region and aria-controls target remain valid.
    return (
        <section
            id={id}
            className="grid gap-[16px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]"
        >
            {collapsible ? (
                // The toggle sits INSIDE the h2, not around it. A <button> takes
                // presentational children in the accessibility tree, so an h2
                // nested in one drops out of the heading list entirely — a
                // screen-reader user navigating Settings by heading finds every
                // other card and not this one. The description stays outside the
                // control so it does not become part of its accessible name.
                <header className="grid gap-[2px]">
                    {eyebrow ? (
                        <span className={`${EYEBROW_CLASS} text-[color:var(--muted)]`}>{eyebrow}</span>
                    ) : null}
                    <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em] text-[color:var(--ink)]">
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            aria-expanded={!collapsed}
                            aria-controls={bodyId}
                            className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-[8px] m-0 border-0 bg-transparent p-0 text-left font-medium text-[15px] leading-[1.06] tracking-[-0.04em] text-[color:var(--ink)] cursor-pointer outline-none"
                        >
                            <span>{title}</span>
                            <span className="pointer-events-none mt-[2px] inline-flex items-center gap-[5px] text-[color:var(--muted)]">
                                <span className={`${EYEBROW_CLASS}`}>{collapsed ? "Show" : "Hide"}</span>
                                <span aria-hidden="true" className="text-[9px] leading-none text-[color:var(--muted-soft)]">
                                    {collapsed ? "▸" : "▾"}
                                </span>
                            </span>
                        </button>
                    </h2>
                    {description ? (
                        <p className={`${NOTE_CLASS} text-[12px] leading-[1.4] mt-[4px]`}>
                            {description}
                        </p>
                    ) : null}
                </header>
            ) : (
                <header className="grid gap-[2px]">{headerInner}</header>
            )}
            {/* Body uses a generous gap so each FormField / toggle
                row clearly reads as its own unit, not a continuous
                stripe of text. */}
            <div id={bodyId} className={collapsed ? "hidden" : "grid gap-[18px]"}>{children}</div>
            {footer ? <div className={collapsed ? "hidden" : "grid"}>{footer}</div> : null}
        </section>
    );
}

function FormField({ label, hint, children }) {
    return (
        <label className="grid gap-[4px]">
            <span className={NOTE_CLASS}>{label}</span>
            {children}
            {hint ? (
                <span className={`${NOTE_CLASS} text-[10px]`}>{hint}</span>
            ) : null}
        </label>
    );
}

const inputCls =
    "min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";

const primaryBtnCls =
    "h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer";

const secondaryBtnCls =
    "h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer";

function StatusLine({ status, error, savedHint = "Saved" }) {
    if (status === "saving") {
        return <span className={`${NOTE_CLASS} text-[11px]`}>Saving…</span>;
    }
    if (status === "saved") {
        return <span className="text-[11px] text-[color:var(--green)]">{savedHint}</span>;
    }
    if (error) {
        return <span className="text-[11px] text-red-400">{error}</span>;
    }
    return <span aria-hidden="true">&nbsp;</span>;
}

// Toggle pill — reuses the chip vocabulary from ColumnPicker /
// TraceLogPanel context controls. Active = green border + green-soft
// bg; inactive = line border + muted.
function ToggleChip({ checked, onChange, label, description }) {
    return (
        // Grid rather than flex: the description sits under the control as part
        // of it, not beside it as a peer.
        <div className="grid gap-[4px]">
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                className={[
                    "flex w-full items-center justify-between gap-[6px] rounded-[2px] border px-[10px] py-[6px] text-left text-[12px] leading-[1.2] transition-colors",
                    "cursor-pointer",
                    checked
                        ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium"
                        : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
                ].join(" ")}
            >
                <span>{label}</span>
                <span
                    aria-hidden="true"
                    className={[
                        "h-[8px] w-[8px] flex-none rounded-[999px] transition-colors",
                        checked ? "bg-[color:var(--green)]" : "bg-[color:var(--line)]",
                    ].join(" ")}
                />
            </button>
            {description ? (
                <span className={`${NOTE_CLASS} text-[10px] leading-[1.3]`}>{description}</span>
            ) : null}
        </div>
    );
}

// ── Endpoint Configuration ───────────────────────────────────────

function defaultEndpointConfig() {
    return {
        hide_tray_icon: false,
        // null = unset → endpoints bind the default port (4318).
        otel_collector_port: null,
    };
}

// The overlay this page sends on save: every field it renders a control for,
// and nothing else (`saveTenantConfiguration` re-reads and preserves the rest).
//
// Exported so a test can assert the payload directly. A control whose state
// this page mutates but omits here is the failure mode worth pinning: the
// merge helper then re-sends the value already on the server, so the toggle
// moves, the save succeeds, and the setting never changes.
export function endpointConfigOverlay(config) {
    return {
        hide_tray_icon: config.hide_tray_icon,
        otel_collector_port: config.otel_collector_port,
    };
}

function flattenConfig(c) {
    if (!c) return defaultEndpointConfig();
    return {
        hide_tray_icon: c.hideTrayIcon ?? c.hide_tray_icon ?? false,
        otel_collector_port: readOtelCollectorPort(c),
    };
}

/** Text shown in the port input for a flattened config value. */
function otelPortText(port) {
    return port == null ? "" : String(port);
}

export function EndpointConfigurationSection() {
    const [config, setConfig] = useState(defaultEndpointConfig);
    const [savedConfig, setSavedConfig] = useState(defaultEndpointConfig);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    // Raw text of the port input, kept separate from `config` so a transient
    // mid-typing value doesn't have to parse; `config.otel_collector_port` only
    // advances on a valid parse, and an invalid text blocks Save below.
    const [portText, setPortText] = useState("");

    // Include portText so an invalid edit (which doesn't advance config) still
    // counts as dirty and keeps Discard enabled.
    const dirty = useMemo(
        () =>
            JSON.stringify(config) !== JSON.stringify(savedConfig) ||
            portText !== otelPortText(savedConfig.otel_collector_port),
        [config, savedConfig, portText],
    );
    const portParse = useMemo(() => parseOtelCollectorPortInput(portText), [portText]);

    const refetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await restGet("/v1/tenant/configuration");
            const next = flattenConfig(resp?.configuration);
            setConfig(next);
            setSavedConfig(next);
            setPortText(otelPortText(next.otel_collector_port));
            const ts = resp?.updatedAt ?? resp?.updated_at ?? null;
            setUpdatedAt(ts ? new Date(ts) : null);
        } catch (err) {
            console.error("[settings] tenant config", err);
            setError(err.message || String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        refetch().catch(() => {});
        return () => { cancelled = true; };
    }, [refetch]);

    const onSave = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            const json = await saveTenantConfiguration(endpointConfigOverlay(config));
            const saved = flattenConfig(json?.configuration ?? config);
            setConfig(saved);
            setSavedConfig(saved);
            setPortText(otelPortText(saved.otel_collector_port));
            const ts = json?.updatedAt ?? json?.updated_at ?? null;
            setUpdatedAt(ts ? new Date(ts) : new Date());
            setStatus("saved");
            setTimeout(() => setStatus(null), 1800);
        } catch (err) {
            setStatus(null);
            setError(err.message || String(err));
        }
    }, [config]);

    const setTop = (k, v) => setConfig((cur) => ({ ...cur, [k]: v }));

    return (
        <SectionCard
            id="settings-endpoint-config"
            eyebrow="Fleet"
            title="Endpoint Configuration"
            description="Agent settings pushed to every endpoint in this tenant. Changes apply on the next agent heartbeat."
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <div className="flex items-center gap-[10px]">
                        <StatusLine status={status} error={error} />
                        {updatedAt ? (
                            <span className={`${NOTE_CLASS} text-[10px]`}>
                                Last saved {dayLabelFormatter.format(updatedAt)}{" "}
                                {timeLabelFormatter.format(updatedAt)}
                            </span>
                        ) : null}
                    </div>
                    <div className="flex items-center gap-[6px]">
                        <button
                            type="button"
                            onClick={() => {
                                setConfig(savedConfig);
                                setPortText(otelPortText(savedConfig.otel_collector_port));
                            }}
                            disabled={!dirty || status === "saving"}
                            className={secondaryBtnCls}
                        >
                            Discard
                        </button>
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={!dirty || status === "saving" || loading || !portParse.ok}
                            className={dirty ? primaryBtnCls : secondaryBtnCls}
                        >
                            Save
                        </button>
                    </div>
                </div>
            }
        >
            {/* Toggles are direct children of the SectionCard body
                so they pick up its `gap-[18px]` spacing — gives each
                toggle + description pair clear separation from the
                next, instead of bleeding into one continuous block
                of text. */}
            <ToggleChip
                checked={config.hide_tray_icon}
                onChange={(v) => setTop("hide_tray_icon", v)}
                label="Hide system tray icon"
                description="Suppresses the tray process and removes the auto-start entry. Default: off."
            />
            <FormField
                label="OTLP collector port"
                hint={
                    portParse.ok
                        ? `The agent's AI telemetry collector uses this loopback port on each endpoint. Leave it empty to use ${DEFAULT_OTEL_COLLECTOR_PORT}. Choose another port if an OTel collector such as Datadog, Alloy, or Dynatrace already uses ${DEFAULT_OTEL_COLLECTOR_PORT}. Changes apply after the agent restarts. Changing the port can briefly pause capture on macOS until the agent reconnects. A port conflict prevents AI telemetry capture on that host, so coordinate this setting with your endpoint owners.`
                        : null
                }
            >
                <input
                    type="text"
                    inputMode="numeric"
                    value={portText}
                    onChange={(e) => {
                        const text = e.target.value;
                        setPortText(text);
                        const parsed = parseOtelCollectorPortInput(text);
                        if (parsed.ok) setTop("otel_collector_port", parsed.value);
                    }}
                    placeholder={`${DEFAULT_OTEL_COLLECTOR_PORT} (default)`}
                    min={MIN_OTEL_COLLECTOR_PORT}
                    max={MAX_OTEL_COLLECTOR_PORT}
                    className={inputCls}
                    aria-invalid={!portParse.ok}
                />
                {!portParse.ok ? (
                    <span className="text-[10px] text-red-400">{portParse.error}</span>
                ) : null}
            </FormField>
        </SectionCard>
    );
}

// ── Password Management ──────────────────────────────────────────

export function PasswordSection() {
    useSyncExternalStore(
        onAuthIdentityChange,
        getAuthIdentityEpoch,
        getAuthIdentityEpoch,
    );

    if (getUserMetadata()?.authenticationMethod !== "AUTHENTICATION_METHOD_PASSWORD") {
        return null;
    }

    return <PasswordForm />;
}

function PasswordForm() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);

    const onSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (next.length < 8) {
            setError("New password must be at least 8 characters.");
            return;
        }
        if (next !== confirm) {
            setError("New password and confirmation don't match.");
            return;
        }
        setStatus("saving");
        setError(null);
        try {
            await restPost("/v1/user-management/change-password", {
                current_password: current,
                new_password: next,
            });
            setCurrent(""); setNext(""); setConfirm("");
            setStatus("saved");
            setTimeout(() => setStatus(null), 2000);
        } catch (err) {
            setStatus(null);
            setError(err.message || String(err));
        }
    }, [current, next, confirm]);

    const canSubmit = current.length > 0 && next.length > 0 && confirm.length > 0;

    return (
        <SectionCard
            id="settings-password"
            eyebrow="Account"
            title="Change Password"
            description="This only applies if you sign in with a password. If you use single sign-on, manage your credentials with your identity provider."
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <StatusLine status={status} error={error} savedHint="Password updated" />
                    <button
                        type="submit"
                        form="settings-password-form"
                        className={canSubmit ? primaryBtnCls : secondaryBtnCls}
                        disabled={!canSubmit || status === "saving"}
                    >
                        Update password
                    </button>
                </div>
            }
        >
            <form id="settings-password-form" onSubmit={onSubmit} className="grid gap-[18px]">
                <FormField label="Current password">
                    <input
                        type="password"
                        autoComplete="current-password"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        className={inputCls}
                    />
                </FormField>
                <FormField label="New password" hint="At least 8 characters.">
                    <input
                        type="password"
                        autoComplete="new-password"
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        className={inputCls}
                    />
                </FormField>
                <FormField label="Confirm new password">
                    <input
                        type="password"
                        autoComplete="new-password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className={inputCls}
                    />
                </FormField>
            </form>
        </SectionCard>
    );
}

// ── Clear local data ─────────────────────────────────────────────
//
// Per-browser, per-tenant wipe of the same three buckets the logout
// modal offers, for users who want to clean up without signing out.
// Cobalt finding PT38031_6. Each row has its own confirm dialog so a
// stray click never wipes data unintentionally.

const CLEAR_BUCKETS = [
    {
        key: "chat",
        label: "Chat history",
        description: CHAT_DATA_CLEAR_COPY.description,
        confirmTitle: "Clear chat history?",
        confirmBody: CHAT_DATA_CLEAR_COPY.confirmation,
        run: () => clearChatData(),
    },
    {
        key: "workflows",
        label: "Workflows",
        description: "Workflow definitions and run history saved in this browser.",
        confirmTitle: "Clear workflows?",
        confirmBody: "Workflow definitions and run history in this browser will be deleted. Server-side data is unaffected.",
        run: () => clearWorkflowData(),
    },
    {
        key: "mcp",
        label: "MCP credentials",
        description: "OAuth tokens and API keys for Linear, Notion, Zapier, and other MCP servers.",
        confirmTitle: "Clear MCP credentials?",
        confirmBody: "All saved MCP connections in this browser will be removed. You'll need to reconnect to use them again.",
        run: () => { clearMcpCredentials(); return Promise.resolve(); },
    },
];

export function ClearLocalDataSection() {
    // Per-bucket status: null | "clearing" | "cleared" | { error: string }
    const [statuses, setStatuses] = useState({});
    const [pending, setPending] = useState(null); // bucket key awaiting confirmation

    const runClear = useCallback(async (bucket) => {
        setPending(null);
        setStatuses((s) => ({ ...s, [bucket.key]: "clearing" }));
        try {
            // No tenantId passed: clearUserData resolves it from /v1/me.
            // Chat clears notify ChatConsole via subscribeChatsCleared so
            // its in-memory transcript empties too - no reload needed.
            await bucket.run();
            setStatuses((s) => ({ ...s, [bucket.key]: "cleared" }));
            setTimeout(() => {
                setStatuses((s) => {
                    if (s[bucket.key] !== "cleared") return s;
                    const next = { ...s }; delete next[bucket.key]; return next;
                });
            }, 2500);
        } catch (err) {
            setStatuses((s) => ({ ...s, [bucket.key]: { error: err?.message || String(err) } }));
        }
    }, []);

    const pendingBucket = pending ? CLEAR_BUCKETS.find((b) => b.key === pending) : null;

    return (
        <SectionCard
            id="settings-clear-local-data"
            eyebrow="Local Storage"
            title="Clear local data"
            description="Wipe locally-cached data from this browser without signing out. Affects this browser only; server-side state is unchanged."
        >
            {CLEAR_BUCKETS.map((bucket) => {
                const status = statuses[bucket.key];
                const busy = status === "clearing";
                return (
                    <div
                        key={bucket.key}
                        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[10px]"
                    >
                        <div className="grid gap-[2px] min-w-0">
                            <span className="text-[12px] leading-[1.2] text-[color:var(--ink)]">{bucket.label}</span>
                            <span className={`${NOTE_CLASS} text-[10px] leading-[1.3]`}>{bucket.description}</span>
                        </div>
                        <div className="flex items-center gap-[10px]">
                            <ClearStatus status={status} />
                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => setPending(bucket.key)}
                                className="h-[26px] rounded-[2px] border border-[color:var(--red,#c0392b)] bg-transparent px-[10px] text-[10px] leading-[1] tracking-[0.04em] uppercase text-[color:var(--red,#c0392b)] font-medium hover:bg-[color:var(--red,#c0392b)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                );
            })}
            <ConfirmDialog
                open={pendingBucket != null}
                analyticsId={DIALOG.LOCAL_DATA_CLEAR}
                eyebrow="Clear local data"
                title={pendingBucket?.confirmTitle ?? ""}
                body={pendingBucket?.confirmBody ?? ""}
                confirmLabel="Clear"
                destructive
                onConfirm={() => pendingBucket && runClear(pendingBucket)}
                onCancel={() => setPending(null)}
            />
        </SectionCard>
    );
}

function ClearStatus({ status }) {
    if (status === "clearing") {
        return <span className={`${NOTE_CLASS} text-[10px]`}>Clearing…</span>;
    }
    if (status === "cleared") {
        return <span className="text-[10px] text-[color:var(--green)]">Cleared</span>;
    }
    if (status && typeof status === "object" && status.error) {
        return <span className="text-[10px] text-red-400">{status.error}</span>;
    }
    return null;
}

// ── Page ─────────────────────────────────────────────────────────

// ── Custom AI Providers (O-697 / O-1164) ─────────────────────────
//
// Tenant-authored capture rules: each rule tells the endpoint agent to
// capture traffic to an AI provider outside the built-in set and
// attribute it under `provider_tag`. Rules live in the same
// TenantConfiguration as the endpoint toggles, so the save routes
// through saveTenantConfiguration (full known-field set, snake_case).

const NEW_RULE = () => ({
    id: `rule-${Date.now()}`,
    provider_tag: "",
    domain_patterns: [newDomainPattern("exact", "")],
    // Path is no longer an admin-facing filter: a custom provider recognizes
    // every request on its configured hostnames. Keep the hidden catch-all on
    // the wire until all released servers and agents accept an empty list.
    path_patterns: [newPathPattern("/")],
    method: "CUSTOM_PROVIDER_HTTP_METHOD_UNSPECIFIED",
    strategy: "CUSTOM_CAPTURE_STRATEGY_FULL_BODY",
});

// Anthropic key (O-1700). Admin-only. The key is write-only: the GET returns a
// "configured / not configured" status, never the secret, so we never
// round-trip the value back into the input.
//
// The route, the key's name and the API's name stay "compliance": they are
// Anthropic's own spelling and the admin has to find them in claude.ai.
function AnthropicApiKeySectionContent({ showPermissionUnavailable = false }) {
    const caps = useMyPermissions();
    const [apiKey, setApiKey] = useState("");
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const {
        authorized,
        integration: summary,
        requestStatus,
        requestError,
        refresh,
    } = useComplianceIntegrationHealth();

    const onSave = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            await restPost("/v1/compliance-integration:set-key", {
                api_key: apiKey.trim(),
            });
            setApiKey("");
            setStatus("saved");
            await refresh({ afterCurrent: true });
            setTimeout(() => setStatus(null), 1800);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [apiKey, refresh, caps]);

    const onRemove = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            await restDelete("/v1/compliance-integration");
            setApiKey("");
            await refresh({ afterCurrent: true });
            setStatus(null);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [refresh, caps]);

    // The RPC remains fail-closed while permission verification is degraded.
    // A mixed-permission page can opt into bounded posture copy without
    // exposing the key controls before the admin-equivalent grant is known.
    if (!authorized) {
        const permissionPending = !caps || caps.loading;
        const permissionUnavailable = caps?.degraded || caps?.subjectScopeDegraded;
        if (!showPermissionUnavailable || (!permissionPending && !permissionUnavailable)) return null;
        return (
            <SectionCard
                id="settings-compliance-api-key"
                eyebrow="Integrations"
                title="Anthropic Compliance API key"
                description="Import claude.ai and Claude Desktop chats through Anthropic's Compliance API."
            >
                <div aria-busy={permissionPending ? "true" : undefined}>
                    <p className={NOTE_CLASS} role="status">
                        {permissionPending
                            ? "Checking whether your account can manage Anthropic capture."
                            : "Anthropic settings are temporarily unavailable while Origin verifies your permissions."}
                    </p>
                </div>
            </SectionCard>
        );
    }

    const canSubmit = apiKey.trim().length > 0 && status !== "saving";
    const configured = summary?.configured ?? false;
    const loading = (requestStatus === "idle" || requestStatus === "loading") && !summary;
    const health = complianceHealthPresentation(summary);
    const healthTone = health.tone === "critical"
        ? "border-red-500/50 bg-red-500/10"
        : health.tone === "warning"
            ? "border-amber-500/50 bg-amber-500/10"
            : health.tone === "healthy"
                ? "border-[color:var(--line)] bg-[color:var(--control-bg)]"
                : "border-[color:var(--line)] bg-[color:var(--control-bg)]";

    return (
        <SectionCard
            id="settings-anthropic-api-key"
            eyebrow="Integrations"
            title="Anthropic (claude.ai and Claude Desktop)"
            description="Import the chats your people have in claude.ai and Claude Desktop, so they appear alongside the prompts captured on the endpoint. Paste a Compliance Access Key (sk-ant-api01-...) created in claude.ai."
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <StatusLine status={status} error={error} savedHint="Key saved" />
                    <div className="flex items-center gap-[8px]">
                        {configured ? (
                            <button type="button" onClick={onRemove} className={secondaryBtnCls}>
                                Remove
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={!canSubmit}
                            className={canSubmit ? primaryBtnCls : secondaryBtnCls}
                        >
                            {configured ? "Replace key" : "Save key"}
                        </button>
                    </div>
                </div>
            }
        >
            <div aria-busy={loading ? "true" : undefined}>
                {loading ? (
                    <p className={NOTE_CLASS}>Loading...</p>
                ) : (
                    <>
                        {requestStatus === "unavailable" ? (
                            <p className={`${NOTE_CLASS} mb-[10px]`} role="status">
                                {requestError || "Integration status is temporarily unavailable."}
                                {summary ? " The last confirmed connector status is shown below." : ""}
                            </p>
                        ) : null}
                        {requestStatus === "unavailable" && !summary ? null : (
                            <div
                                data-compliance-health-status={summary?.healthStatus ?? "unconfigured"}
                                className={`mb-[12px] grid gap-[8px] border px-[10px] py-[9px] ${healthTone}`}
                                aria-live="polite"
                            >
                                <div className="flex items-baseline justify-between gap-[12px]">
                                    <strong className="flex items-center gap-[6px] text-[12px] font-medium text-[color:var(--ink)]">
                                        {health.tone === "healthy" ? (
                                            <span className="h-[5px] w-[5px] rounded-full bg-emerald-500/70" aria-hidden="true" />
                                        ) : null}
                                        {health.label}
                                    </strong>
                                    {requestStatus === "refreshing" ? (
                                        <span className={`${NOTE_CLASS} text-[10px]`}>Refreshing...</span>
                                    ) : null}
                                </div>
                                <p className={`${NOTE_CLASS} text-[11px] leading-[1.4]`}>{health.impact}</p>
                                {configured ? (
                                    <dl className="grid grid-cols-1 gap-[5px] text-[10px] text-[color:var(--muted)] sm:grid-cols-3">
                                        <div>
                                            <dt className="uppercase tracking-[0.05em]">Last check</dt>
                                            <dd className="mt-[1px] text-[color:var(--ink)]">{formatComplianceHealthTime(summary.lastCheckedAt)}</dd>
                                        </div>
                                        <div>
                                            <dt className="uppercase tracking-[0.05em]">Last success</dt>
                                            <dd className="mt-[1px] text-[color:var(--ink)]">{formatComplianceHealthTime(summary.lastSuccessAt)}</dd>
                                        </div>
                                        <div>
                                            <dt className="uppercase tracking-[0.05em]">Last failure</dt>
                                            <dd className="mt-[1px] text-[color:var(--ink)]">{formatComplianceHealthTime(summary.lastErrorAt)}</dd>
                                        </div>
                                    </dl>
                                ) : null}
                            </div>
                        )}
                        <FormField
                            label="Anthropic Compliance Access Key"
                            hint="Created in claude.ai. Stored encrypted; never shown again after saving."
                        >
                            <input
                                type="password"
                                value={apiKey}
                                autoComplete="off"
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="sk-ant-..."
                                className={inputCls}
                            />
                        </FormField>
                    </>
                )}
            </div>
        </SectionCard>
    );
}

export function AnthropicApiKeySection({ showPermissionUnavailable = false }) {
    return (
        <ComplianceIntegrationHealthProvider>
            <AnthropicApiKeySectionContent showPermissionUnavailable={showPermissionUnavailable} />
        </ComplianceIntegrationHealthProvider>
    );
}

function CopyValue({ label, value }) {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    }, [value]);

    return (
        <FormField label={label}>
            <div className="flex gap-[8px]">
                <input
                    aria-label={label}
                    type="text"
                    readOnly
                    value={value}
                    className={`${inputCls} min-w-0 flex-1 font-mono`}
                />
                <button type="button" onClick={copy} className={secondaryBtnCls}>
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
        </FormField>
    );
}

export function PerplexityAuditLogSection() {
    const caps = useMyPermissions();
    const capsRef = useLatestRef(caps);
    const [summary, setSummary] = useState(null);
    const [oneTimeCredential, setOneTimeCredential] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const refetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setSummary(perplexitySummary(await getPerplexityAuditIntegration()));
        } catch (err) {
            setError(denialCopy(err, capsRef.current).message);
        } finally {
            setLoading(false);
        }
    }, [capsRef]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    const saveResponse = useCallback((response) => {
        const next = perplexitySummary(response);
        setSummary(next);
        setOneTimeCredential(
            next.webhookToken
                ? { webhookUrl: next.webhookUrl, webhookToken: next.webhookToken }
                : null,
        );
    }, []);

    const create = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            saveResponse(await createPerplexityAuditIntegration());
            setStatus("saved");
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [caps, saveResponse]);

    const rotate = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            saveResponse(await rotatePerplexityAuditCredential());
            setStatus("saved");
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [caps, saveResponse]);

    const remove = useCallback(async () => {
        setConfirmRemove(false);
        setStatus("saving");
        setError(null);
        try {
            await deletePerplexityAuditIntegration();
            setOneTimeCredential(null);
            setSummary(perplexitySummary(null));
            setStatus(null);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [caps]);

    const configured = summary?.configured ?? false;
    const busy = status === "saving";

    return (
        <SectionCard
            id="settings-perplexity-audit-log"
            eyebrow="Integrations"
            title="Perplexity Enterprise Audit Logs"
            description="Import Perplexity prompts, responses, and automated agent actions through an authenticated webhook. Create the webhook here, then paste its URL and bearer token into the Perplexity admin console."
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <StatusLine status={status} error={error} savedHint="Webhook ready" />
                    <div className="flex items-center gap-[8px]">
                        {configured ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setConfirmRemove(true)}
                                    disabled={busy}
                                    className={secondaryBtnCls}
                                >
                                    Remove
                                </button>
                                <button
                                    type="button"
                                    onClick={rotate}
                                    disabled={busy}
                                    className={secondaryBtnCls}
                                >
                                    Rotate token
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={create}
                                disabled={busy || loading}
                                className={primaryBtnCls}
                            >
                                Create webhook
                            </button>
                        )}
                    </div>
                </div>
            }
        >
            <div aria-busy={loading ? "true" : undefined} className="grid gap-[14px]">
                {loading && !summary ? (
                    <p className={NOTE_CLASS}>Loading...</p>
                ) : error && !summary ? (
                    <div className="grid gap-[8px] justify-items-start">
                        <p className={NOTE_CLASS}>Could not load the Perplexity connection.</p>
                        <button type="button" onClick={refetch} className={secondaryBtnCls}>
                            Retry
                        </button>
                    </div>
                ) : configured ? (
                    <>
                        <p className={NOTE_CLASS}>
                            {summary.status ? `Status: ${summary.status}.` : "Connected."} Authorized audit
                            payloads are retained for {summary.rawRetentionDays} days.
                        </p>
                        <CopyValue label="Webhook URL" value={summary.webhookUrl} />
                        {oneTimeCredential ? (
                            <div className="grid gap-[10px] border border-[color:var(--green)] bg-[color:var(--green-soft)] p-[10px]">
                                <p className="m-0 text-[12px] leading-[1.4] text-[color:var(--ink)]">
                                    Copy this bearer token now. Origin will not show it again.
                                </p>
                                <CopyValue
                                    label="Bearer token"
                                    value={oneTimeCredential.webhookToken}
                                />
                                <p className={NOTE_CLASS}>
                                    In Perplexity, use the webhook URL above and send this value as
                                    the Authorization header: Bearer &lt;token&gt;.
                                </p>
                            </div>
                        ) : null}
                        {summary.previousCredentialValidUntil ? (
                            <p className={NOTE_CLASS}>
                                The previous token remains valid until {new Date(
                                    summary.previousCredentialValidUntil,
                                ).toLocaleString()}.
                            </p>
                        ) : null}
                    </>
                ) : (
                    <p className={NOTE_CLASS}>
                        Not configured. Creating a webhook generates a URL and one-time bearer
                        token for the Perplexity admin console.
                    </p>
                )}
            </div>
            <ConfirmDialog
                open={confirmRemove}
                analyticsId={DIALOG.PERPLEXITY_WEBHOOK_REMOVE}
                eyebrow="Perplexity Audit Logs"
                title="Remove the Perplexity webhook?"
                body="Perplexity deliveries will stop immediately. Previously retained audit events are not deleted."
                confirmLabel="Remove webhook"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(false)}
            />
        </SectionCard>
    );
}

function langsmithStatusLabel(status) {
    switch (status) {
        case "connected":
            return "Connected.";
        case "pending":
            return "Waiting on its first delivery.";
        case "error":
            return "Not capturing.";
        case "disabled":
            return "Disabled.";
        default:
            return "";
    }
}

export function LangSmithSection() {
    const caps = useMyPermissions();
    const capsRef = useLatestRef(caps);
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [apiKey, setApiKey] = useState("");
    const [workspaceId, setWorkspaceId] = useState("");
    const [status, setStatus] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projectsError, setProjectsError] = useState(null);
    const [selection, setSelection] = useState(() => new Set());
    const [projectStatus, setProjectStatus] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const openRequest = useRef(null);

    const refetch = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await listLangSmithIntegrations();
            setIntegrations((response?.integrations ?? []).map(langsmithIntegration));
        } catch (err) {
            setLoadError(denialCopy(err, capsRef.current).message);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, [capsRef]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    const openPicker = useCallback(
        async (integration) => {
            if (openId === integration.id) {
                openRequest.current = null;
                setOpenId(null);
                return;
            }
            openRequest.current = integration.id;
            setOpenId(integration.id);
            setProjects([]);
            setProjectStatus(null);
            setProjectsError(null);
            setProjectsLoading(true);
            setSelection(new Set(integration.watchedProjects.map((project) => project.projectId)));
            try {
                const response = await listLangSmithProjects(integration.id);
                if (openRequest.current !== integration.id) return;
                setProjects(
                    (response?.projects ?? []).map((project) => ({
                        projectId: project?.projectId ?? project?.project_id ?? "",
                        displayName: project?.displayName ?? project?.display_name ?? "",
                    })),
                );
            } catch (err) {
                if (openRequest.current !== integration.id) return;
                setProjectsError(denialOrRawMessage(err, caps));
            } finally {
                if (openRequest.current === integration.id) setProjectsLoading(false);
            }
        },
        [caps, openId],
    );

    const create = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            await createLangSmithIntegration({
                apiKey: apiKey.trim(),
                workspaceId: workspaceId.trim(),
            });
            setApiKey("");
            setWorkspaceId("");
            setStatus("saved");
            await refetch();
            setTimeout(() => setStatus(null), 1800);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [apiKey, caps, refetch, workspaceId]);

    const saveSelection = useCallback(
        async (integrationId) => {
            setProjectStatus("saving");
            setProjectsError(null);
            try {
                await setLangSmithWatchedProjects(integrationId, [...selection]);
                setProjectStatus("saved");
                await refetch();
                setTimeout(() => setProjectStatus(null), 1800);
            } catch (err) {
                setProjectStatus(null);
                setProjectsError(denialOrRawMessage(err, caps));
            }
        },
        [caps, refetch, selection],
    );

    const remove = useCallback(async () => {
        const target = confirmRemove;
        setConfirmRemove(null);
        if (!target) return;
        setStatus("saving");
        setError(null);
        try {
            await deleteLangSmithIntegration(target.id);
            if (openId === target.id) setOpenId(null);
            await refetch();
            setStatus(null);
        } catch (err) {
            setStatus(null);
            setError(denialOrRawMessage(err, caps));
        }
    }, [caps, confirmRemove, openId, refetch]);

    const toggleProject = useCallback((projectId) => {
        setSelection((current) => {
            const next = new Set(current);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            return next;
        });
    }, []);

    const canCreate =
        apiKey.trim().length > 0 && workspaceId.trim().length > 0 && status !== "saving";

    return (
        <SectionCard
            id="settings-langsmith"
            eyebrow="Integrations"
            title="LangSmith (LangChain and LangGraph agents)"
            description="Capture prompts from agents your own teams build. Paste a workspace-scoped LangSmith service key, then pick which tracing projects to watch. Origin puts an automation rule on each project you pick, and LangSmith posts new runs to us as they finish."
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <StatusLine status={status} error={error} savedHint="Workspace connected" />
                    <button
                        type="button"
                        onClick={create}
                        disabled={!canCreate}
                        className={canCreate ? primaryBtnCls : secondaryBtnCls}
                    >
                        Connect workspace
                    </button>
                </div>
            }
        >
            <div aria-busy={loading ? "true" : undefined} className="grid gap-[14px]">
                <div className="grid gap-[10px] sm:grid-cols-2">
                    <FormField
                        label="Service key"
                        hint="Scope it to one workspace. It needs to read runs and projects, create automation rules, and read organization members. Stored encrypted and never shown again."
                    >
                        <input
                            type="password"
                            value={apiKey}
                            autoComplete="off"
                            onChange={(event) => setApiKey(event.target.value)}
                            placeholder="lsv2_sk_…"
                            className={inputCls}
                        />
                    </FormField>
                    <FormField
                        label="Workspace ID"
                        hint="LangSmith shows this under Settings, Workspaces. The key must belong to this workspace or connecting fails."
                    >
                        <input
                            type="text"
                            value={workspaceId}
                            onChange={(event) => setWorkspaceId(event.target.value)}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className={inputCls}
                        />
                    </FormField>
                </div>
                <div className="grid gap-[10px] border-t border-[color:var(--line)] pt-[12px]">
                    <h3 className="m-0 text-[13px] font-medium">Connected workspaces</h3>
                    {loading && !loaded ? (
                        <p className={NOTE_CLASS}>Loading…</p>
                    ) : loadError && integrations.length === 0 ? (
                        <div className="grid gap-[8px] justify-items-start">
                            <p className={NOTE_CLASS}>Could not load LangSmith workspaces.</p>
                            <button type="button" onClick={refetch} className={secondaryBtnCls}>
                                Retry
                            </button>
                        </div>
                    ) : integrations.length === 0 ? (
                        <p className={NOTE_CLASS}>
                            None yet. A key reaches one LangSmith workspace, so connect each
                            workspace whose agents you want captured.
                        </p>
                    ) : (
                        integrations.map((integration) => {
                            const watchedById = new Map(
                                integration.watchedProjects.map((project) => [
                                    project.projectId,
                                    project,
                                ]),
                            );
                            const open = openId === integration.id;
                            const lastPoll = whenLabel(integration.lastPollAt);
                            return (
                                <div
                                    key={integration.id}
                                    className="grid gap-[8px] border border-[color:var(--line)] p-[10px]"
                                >
                                    <div className="flex flex-wrap items-baseline justify-between gap-[8px]">
                                        <span className="font-mono text-[12px] text-[color:var(--ink)]">
                                            {integration.workspaceId}
                                        </span>
                                        <div className="flex items-center gap-[8px]">
                                            <button
                                                type="button"
                                                onClick={() => openPicker(integration)}
                                                className={secondaryBtnCls}
                                            >
                                                {open ? "Close" : "Choose projects"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setConfirmRemove(integration)}
                                                className={secondaryBtnCls}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                    <p className={NOTE_CLASS}>
                                        {langsmithStatusLabel(integration.status)
                                            ? `${langsmithStatusLabel(integration.status)} `
                                            : ""}
                                        {integration.watchedProjects.length === 0
                                            ? "No projects watched, so nothing is being captured from this workspace."
                                            : `${integration.watchedProjects.length} project${
                                                  integration.watchedProjects.length === 1 ? "" : "s"
                                              } watched.`}
                                        {lastPoll
                                            ? ` Last delivery ${lastPoll}.`
                                            : " No deliveries yet."}
                                    </p>
                                    {integration.lastSyncError ? (
                                        <p className="m-0 text-[11px] text-red-400">
                                            {integration.lastSyncError}
                                        </p>
                                    ) : null}
                                    {open ? (
                                        <div
                                            aria-busy={projectsLoading ? "true" : undefined}
                                            className="grid gap-[10px] border-t border-[color:var(--line)] pt-[10px]"
                                        >
                                            {projectsLoading ? (
                                                <p className={NOTE_CLASS}>Reading the workspace…</p>
                                            ) : projectsError && projects.length === 0 ? (
                                                <p className="m-0 text-[11px] text-red-400">
                                                    {projectsError}
                                                </p>
                                            ) : projects.length === 0 ? (
                                                <p className={NOTE_CLASS}>
                                                    This workspace has no tracing projects yet. One
                                                    appears here once an agent traces into it.
                                                </p>
                                            ) : (
                                                <>
                                                    {projects.map((project) => {
                                                        const watched = watchedById.get(
                                                            project.projectId,
                                                        );
                                                        const lastRun = whenLabel(
                                                            watched?.lastWindowEnd,
                                                        );
                                                        return (
                                                            <label
                                                                key={project.projectId}
                                                                className="flex items-start gap-[8px] text-[12px] leading-[1.3] text-[color:var(--ink)]"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selection.has(
                                                                        project.projectId,
                                                                    )}
                                                                    onChange={() =>
                                                                        toggleProject(
                                                                            project.projectId,
                                                                        )
                                                                    }
                                                                    className="mt-[2px]"
                                                                />
                                                                <span className="grid gap-[2px]">
                                                                    <span>
                                                                        {project.displayName ||
                                                                            project.projectId}
                                                                    </span>
                                                                    {watched ? (
                                                                        <span className={NOTE_CLASS}>
                                                                            {lastRun
                                                                                ? `Runs received up to ${lastRun}.`
                                                                                : "Watched. Nothing delivered yet."}
                                                                            {watched.ruleId
                                                                                ? ` Rule ${watched.ruleId}.`
                                                                                : ""}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                    <p className={NOTE_CLASS}>
                                                        Unticking a project deletes its automation
                                                        rule in LangSmith and stops capture for it.
                                                        Runs already captured stay.
                                                    </p>
                                                    <div className="flex items-center justify-between gap-[10px]">
                                                        <StatusLine
                                                            status={projectStatus}
                                                            error={projectsError}
                                                            savedHint="Projects updated"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                saveSelection(integration.id)
                                                            }
                                                            disabled={projectStatus === "saving"}
                                                            className={primaryBtnCls}
                                                        >
                                                            Save projects
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
            <ConfirmDialog
                open={Boolean(confirmRemove)}
                analyticsId={DIALOG.LANGSMITH_DISCONNECT}
                eyebrow="LangSmith"
                title="Disconnect this workspace?"
                body="Every automation rule Origin created in this workspace is deleted and capture stops. Runs already captured are kept. Reconnecting means pasting the key again and picking the projects again."
                confirmLabel="Disconnect"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(null)}
            />
        </SectionCard>
    );
}

export function CustomProvidersSection({ embedded = false, onSaved = null }) {
    const [rules, setRules] = useState([]);
    const [savedRules, setSavedRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);

    const refetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await restGet("/v1/tenant/configuration");
            const cfg = resp?.configuration ?? {};
            const next = normalizeRules(cfg.customProviderRules ?? cfg.custom_provider_rules);
            setRules(next);
            setSavedRules(next);
        } catch (err) {
            console.error("[settings] custom providers", err);
            setError(err.message || String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refetch().catch(() => {}); }, [refetch]);

    const errs = useMemo(() => validateCustomProviderRules(rules), [rules]);
    const hasErrors = useMemo(() => hasAnyValidationError(errs, rules.length), [errs, rules.length]);
    const dirty = useMemo(
        () => JSON.stringify(rules) !== JSON.stringify(savedRules),
        [rules, savedRules],
    );

    const onSave = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            const json = await saveTenantConfiguration({ custom_provider_rules: rules });
            const cfg = json?.configuration ?? {};
            const saved = normalizeRules(cfg.customProviderRules ?? cfg.custom_provider_rules) ;
            const next = saved.length || !rules.length ? saved : rules;
            setRules(next);
            setSavedRules(next);
            setStatus("saved");
            onSaved?.();
            setTimeout(() => setStatus(null), 1800);
        } catch (err) {
            setStatus(null);
            setError(err.message || String(err));
        }
    }, [rules, onSaved]);

    const updateRule = (idx, patch) =>
        setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const removeRule = (idx) => {
        const r = rules[idx];
        const label = r?.provider_tag ? `'${r.provider_tag}'` : `'${r?.id || "unnamed"}'`;
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Remove the custom provider rule ${label}? Takes effect when you click Save.`)) return;
        setRules((prev) => prev.filter((_, i) => i !== idx));
    };

    return (
        <SectionCard
            id="settings-custom-providers"
            eyebrow="Detection"
            title={embedded ? "Custom detection rules" : "AI Service Detection Rules"}
            description={embedded
                ? "Add provider hostnames that are specific to your organization. Saving a rule adds its provider to the policy list as unsanctioned by default."
                : "Recognize additional AI services from provider hostnames. Domain patterns identify matching traffic, and the provider tag labels that activity. Custom rules cannot replace built-in providers."}
            embedded={embedded}
            footer={
                <div className="flex items-center justify-between gap-[10px]">
                    <StatusLine status={status} error={error} />
                    <div className="flex items-center gap-[6px]">
                        <button
                            type="button"
                            onClick={() => setRules(savedRules)}
                            disabled={!dirty || status === "saving"}
                            className={secondaryBtnCls}
                        >
                            Discard
                        </button>
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={!dirty || hasErrors || status === "saving" || loading}
                            title={hasErrors ? "Fix validation errors before saving" : undefined}
                            className={dirty && !hasErrors ? primaryBtnCls : secondaryBtnCls}
                        >
                            Save
                        </button>
                    </div>
                </div>
            }
        >
            {loading ? (
                <span className={`${NOTE_CLASS} text-[11px]`}>Loading…</span>
            ) : rules.length === 0 ? (
                <div className="grid gap-[8px] justify-items-start">
                    <span className={`${NOTE_CLASS} text-[11px]`}>No custom providers configured.</span>
                    <button type="button" onClick={() => setRules([NEW_RULE()])} className={secondaryBtnCls}>
                        + Add a rule
                    </button>
                </div>
            ) : (
                <div className="grid gap-[12px]">
                    {rules.length > MAX_TENANT_RULES_PER_TENANT ? (
                        <span className="text-[11px] text-red-400">
                            {rules.length} rules exceeds the per-tenant cap of {MAX_TENANT_RULES_PER_TENANT}.
                        </span>
                    ) : null}
                    {rules.map((rule, idx) => {
                        const e = errs[idx] || {};
                        return (
                            <div key={idx} className="grid gap-[10px] border border-[color:var(--line)] p-[10px]">
                                <div className="flex items-start justify-between gap-[8px]">
                                    <div className="grid gap-[4px] flex-1 min-w-0">
                                        <FormField label="Rule ID">
                                            <input
                                                type="text"
                                                className={inputCls}
                                                value={rule.id || ""}
                                                placeholder="e.g. fireworks-anthropic"
                                                onChange={(ev) => updateRule(idx, { id: ev.target.value })}
                                            />
                                        </FormField>
                                        {e.id ? <span className="text-[10px] text-red-400">{e.id}</span> : null}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeRule(idx)}
                                        className={secondaryBtnCls + " mt-[18px]"}
                                        title="Remove rule"
                                    >
                                        Remove
                                    </button>
                                </div>

                                <div className="grid gap-[4px]">
                                    <FormField label="Provider tag">
                                        <input
                                            type="text"
                                            className={inputCls}
                                            value={rule.provider_tag || ""}
                                            placeholder="e.g. fireworks-ai"
                                            onChange={(ev) => updateRule(idx, { provider_tag: ev.target.value.toLowerCase() })}
                                        />
                                    </FormField>
                                    {e.provider_tag ? <span className="text-[10px] text-red-400">{e.provider_tag}</span> : null}
                                </div>

                                <div className="grid gap-[4px]">
                                    <span className={NOTE_CLASS}>Domain patterns. Use bare hostnames without a leading <code>*.</code></span>
                                    <div className="grid gap-[6px]">
                                        {(rule.domain_patterns || []).map((entry, dIdx) => {
                                            const parsed = readDomainPatternEntry(entry) || { variant: "exact", value: "" };
                                            const setVariant = (v) => updateRule(idx, {
                                                domain_patterns: rule.domain_patterns.map((x, j) => (j === dIdx ? newDomainPattern(v, parsed.value) : x)),
                                            });
                                            const setValue = (v) => updateRule(idx, {
                                                domain_patterns: rule.domain_patterns.map((x, j) => (j === dIdx ? newDomainPattern(parsed.variant, v) : x)),
                                            });
                                            return (
                                                <div key={dIdx} className="flex items-center gap-[6px]">
                                                    <div className="w-[170px] shrink-0">
                                                        <Select
                                                            ariaLabel="Domain pattern type"
                                                            value={parsed.variant}
                                                            title={DOMAIN_PATTERN_VARIANTS.find((v) => v.value === parsed.variant)?.help}
                                                            onChange={(v) => setVariant(v)}
                                                            options={DOMAIN_PATTERN_VARIANTS}
                                                        />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        className={inputCls + " flex-1 font-mono"}
                                                        value={parsed.value}
                                                        placeholder={parsed.variant === "wildcardPrefix" ? "-aiplatform.googleapis.com" : "api.fireworks.ai"}
                                                        onChange={(ev) => setValue(ev.target.value.trim())}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => updateRule(idx, { domain_patterns: rule.domain_patterns.filter((_, j) => j !== dIdx) })}
                                                        disabled={rule.domain_patterns.length <= 1}
                                                        className={secondaryBtnCls}
                                                        title="Remove pattern"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            onClick={() => updateRule(idx, { domain_patterns: [...(rule.domain_patterns || []), newDomainPattern("exact", "")] })}
                                            className={secondaryBtnCls + " justify-self-start"}
                                        >
                                            + Add pattern
                                        </button>
                                    </div>
                                    {e.domain_patterns ? <span className="text-[10px] text-red-400">{e.domain_patterns}</span> : null}
                                </div>

                            </div>
                        );
                    })}
                    <button type="button" onClick={() => setRules((prev) => [...prev, NEW_RULE()])} className={secondaryBtnCls + " justify-self-start"}>
                        + Add a rule
                    </button>
                </div>
            )}
        </SectionCard>
    );
}


// How the server reports "this tenant may not configure this surface". No
// producer sends it today - the AI-visibility entitlement that used to was
// retired - so the branch is currently unreachable and kept as the handler for
// whatever refuses next.
const ENTITLEMENT_REFUSAL_CODE = "FAILED_PRECONDITION";
// Codes that mean the write definitely did not commit, so rolling the row back
// to its pre-click value is safe. Anything else — including an absent code (a
// fetch that never got a response, a non-JSON proxy page) — is indeterminate.
const DEFINITE_REFUSALS = new Set([
    "INVALID_ARGUMENT",
    "NOT_FOUND",
    "PERMISSION_DENIED",
    "FAILED_PRECONDITION",
    "UNAUTHENTICATED",
]);

const AI_SERVICE_DISPOSITIONS = [
    { value: "AI_SERVICE_DISPOSITION_SANCTIONED", label: "Sanctioned - approved" },
    { value: "AI_SERVICE_DISPOSITION_UNSANCTIONED", label: "Unsanctioned - show as unapproved" },
    { value: "AI_SERVICE_DISPOSITION_IGNORED", label: "Ignored - omit from AI service views" },
];

const AI_SERVICE_EVIDENCE_SPEC = {
    root: { alias: "o", entity: "ENTITY_TYPE_AI_SERVICE_OBSERVATION" },
    timeRange: { relative: { durationSeconds: String(30 * 86400) } },
    output: {
        dimensions: [{
            field: { stepAlias: "o", field: "DIMENSION_FIELD_AI_SERVICE_OBSERVATION_PROVIDER_TAG" },
            outputAlias: "provider_tag",
        }],
        metrics: [
            {
                outputAlias: "observed_endpoints",
                computed: {
                    field: { stepAlias: "o", field: "DIMENSION_FIELD_AI_SERVICE_OBSERVATION_ENDPOINT_ID" },
                    op: "AGGREGATE_OP_COUNT_DISTINCT",
                },
            },
            {
                outputAlias: "last_observed",
                computed: {
                    field: { stepAlias: "o", field: "DIMENSION_FIELD_AI_SERVICE_OBSERVATION_OBSERVED_AT" },
                    op: "AGGREGATE_OP_MAX",
                },
            },
        ],
    },
    orderBy: [{ outputAlias: "observed_endpoints", direction: "DIRECTION_DESC" }],
    limit: 100,
};

function normalizeAiServicePolicy(policy) {
    const providerTag = policy?.providerTag ?? policy?.provider_tag ?? "";
    return {
        providerTag,
        displayName:
            policy?.displayName ?? policy?.display_name ?? providerTag,
        disposition:
            policy?.disposition ?? "AI_SERVICE_DISPOSITION_UNSANCTIONED",
        updatedAt: policy?.updatedAt ?? policy?.updated_at ?? null,
    };
}

export function AiServicesSection({
    canWrite,
    embedded = false,
    refreshKey = 0,
    collapsible = !embedded,
}) {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // "unentitled" | "unavailable" | null — the two quiet non-error outcomes.
    const [refused, setRefused] = useState(null);
    const [rowState, setRowState] = useState({});
    const [evidenceByProvider, setEvidenceByProvider] = useState({});
    const [evidenceState, setEvidenceState] = useState("loading");
    // `{ providerTag, disposition }` awaiting confirmation, or null.
    const [pendingIgnore, setPendingIgnore] = useState(null);

    // `clearRow` names the provider whose save state the refetch supersedes.
    // Only that row's entry is dropped: clearing all of them would also wipe a
    // DIFFERENT row's in-flight `pending`, re-enabling its Select once the
    // refetch resolves while its own request is still outstanding.
    const load = useCallback(async (clearRow = null) => {
        setLoading(true);
        setError(null);
        setRefused(null);
        if (clearRow) {
            setRowState((current) => {
                const { [clearRow]: _superseded, ...rest } = current;
                return rest;
            });
        }
        try {
            const response = await restGet("/v1/ai-services/policies");
            const rows = Array.isArray(response?.policies)
                ? response.policies.map(normalizeAiServicePolicy)
                : [];
            rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
            setPolicies(rows);
        } catch (err) {
            // The server is the entitlement authority (it accepts both AI
            // visibility keys — a client-side entitlement read here drifted
            // against that set once already). Its failed_precondition refusal
            // is a quiet affordance, not an error card; a route that doesn't
            // exist yet (backend deploy skew) renders nothing rather than a
            // broken panel with a useless Retry.
            //
            // Keyed on the gRPC code, not the message: the refusal text is
            // English prose whose Rust-side test would be updated in the same
            // commit as a reword, so both suites stay green while every
            // unentitled tenant's card flips to a red Retry that can never
            // succeed.
            const message = err.serverMessage || err.message || String(err);
            if (err.grpcCode === ENTITLEMENT_REFUSAL_CODE) {
                setRefused("unentitled");
            } else if ([404, 405, 501].includes(err.status)) {
                setRefused("unavailable");
            } else {
                setError(message);
            }
            setPolicies([]);
        } finally {
            setLoading(false);
        }
    }, [refreshKey]);

    useEffect(() => {
        load().catch(() => {});
    }, [load]);

    useEffect(() => {
        let cancelled = false;
        setEvidenceState("loading");
        runQueryRows(AI_SERVICE_EVIDENCE_SPEC)
            .then((rows) => {
                if (cancelled) return;
                const next = {};
                for (const row of rows) {
                    if (!row?.provider_tag) continue;
                    next[row.provider_tag] = {
                        endpoints: Number(row.observed_endpoints || 0),
                        lastObserved: row.last_observed || null,
                    };
                }
                setEvidenceByProvider(next);
                setEvidenceState("ready");
            })
            .catch(() => {
                if (!cancelled) setEvidenceState("unavailable");
            });
        return () => { cancelled = true; };
    }, []);

    const save = useCallback(async (providerTag, disposition) => {
        const previous = policies.find((policy) => policy.providerTag === providerTag);
        if (!previous) return;
        setPolicies((current) => current.map((policy) =>
            policy.providerTag === providerTag ? { ...policy, disposition } : policy,
        ));
        setRowState((current) => ({ ...current, [providerTag]: { pending: true, error: null } }));
        try {
            const response = await restPost(
                `/v1/ai-services/${encodeURIComponent(providerTag)}:set-policy`,
                { provider_tag: providerTag, disposition },
            );
            const saved = normalizeAiServicePolicy(response?.policy);
            setPolicies((current) => current.map((policy) =>
                policy.providerTag === providerTag
                    ? { ...saved, displayName: previous.displayName }
                    : policy,
            ));
            setRowState((current) => ({ ...current, [providerTag]: { pending: false, error: null } }));
        } catch (err) {
            // The write is a single autocommit upsert with no enclosing
            // transaction, so a lost response (5xx, gateway timeout, aborted
            // fetch) cannot un-commit it. Restoring `previous` there would show
            // the LESS restrictive value while the planner enforces the more
            // restrictive one — and nothing reconciles for the life of the
            // mount. Roll back only on a code that proves nothing committed;
            // otherwise re-read the server's state and say so.
            const definitelyRefused = DEFINITE_REFUSALS.has(err.grpcCode);
            if (definitelyRefused) {
                setPolicies((current) => current.map((policy) =>
                    policy.providerTag === providerTag ? previous : policy,
                ));
                setRowState((current) => ({
                    ...current,
                    [providerTag]: {
                        pending: false,
                        error: err.serverMessage || "Could not save. Try again.",
                    },
                }));
                return;
            }
            // Re-read, then report what the row now shows. `load` drops this
            // row's stale save state, so the note is set after it — a red error
            // left standing next to a freshly-reconciled value is the
            // contradiction this branch exists to avoid.
            await load(providerTag).catch(() => {});
            setRowState((current) => ({
                ...current,
                [providerTag]: {
                    pending: false,
                    error: null,
                    note: "Save result was unclear - showing the server's current value.",
                },
            }));
        }
    }, [policies, load]);

    // Moving a provider to Ignored is not a forward-looking preference: it
    // lowers to a NOT IN on the query leaf, so it also erases months of
    // existing observations from every analyst's results org-wide. The other
    // two transitions only change a label and save on selection as before.
    const onDispositionChange = useCallback((providerTag, disposition) => {
        if (disposition === "AI_SERVICE_DISPOSITION_IGNORED") {
            setPendingIgnore({ providerTag, disposition });
            return;
        }
        save(providerTag, disposition);
    }, [save]);

    const ignoreTarget = pendingIgnore
        ? policies.find((policy) => policy.providerTag === pendingIgnore.providerTag)
        : null;

    // Deploy skew: the backend doesn't serve this surface yet. Render nothing
    // (rather than a permanently-empty card) until it does.
    if (!loading && refused === "unavailable") return null;

    return (
        <SectionCard
            id="settings-ai-services"
            eyebrow={embedded ? undefined : "Policy"}
            title={embedded ? "Provider policy" : "Sanctioned AI"}
            description={embedded
                ? "Mark every fleet-recognized or custom provider as sanctioned, unsanctioned, or ignored."
                : "Set the organization-wide treatment for every recognized AI service. Sanctioned services are approved, unsanctioned services remain visible as unapproved, and ignored services are removed from AI service views and analytics. Evidence covers the last 30 days when your role can read analytics."}
            collapsible={collapsible}
            defaultCollapsed={collapsible}
            embedded={embedded}
        >
            <div aria-busy={loading || undefined} className="grid gap-[18px]">
                {loading ? <span className={NOTE_CLASS}>Loading AI services...</span> : null}
                {!loading && refused === "unentitled" ? (
                    <span className={`${NOTE_CLASS} text-[11px]`}>
                        Additional configuration is not available for this tenant.
                    </span>
                ) : null}
                {!loading && !refused && error ? (
                    <div className="flex items-center gap-[8px] text-[11px] text-red-400">
                        <span>Could not load AI services.</span>
                        <button type="button" onClick={() => load()} className={ACTION_TEXT_CLASS}>Retry</button>
                    </div>
                ) : null}
                {!loading && !refused && !error && policies.length === 0 ? (
                    <span className={NOTE_CLASS}>No AI services are currently recognized.</span>
                ) : null}
                {!loading && !refused && !error && policies.length ? (
                    <div className="hidden grid-cols-[minmax(0,1fr)_110px_150px_minmax(220px,300px)] gap-[10px] border-b border-[color:var(--line)] pb-[5px] text-[9px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)] md:grid">
                        <span>Service</span>
                        <span>Endpoints</span>
                        <span>Last observed</span>
                        <span>Classification</span>
                    </div>
                ) : null}
                {!loading && !refused && !error ? policies.map((policy) => {
                    const state = rowState[policy.providerTag] ?? {};
                    const evidence = evidenceByProvider[policy.providerTag];
                    const ignored = policy.disposition === "AI_SERVICE_DISPOSITION_IGNORED";
                    const lastObserved = evidence?.lastObserved ? new Date(evidence.lastObserved) : null;
                    const evidenceLabel = ignored
                        ? "Hidden by policy"
                        : evidenceState === "loading"
                            ? "Loading..."
                            : evidenceState === "unavailable"
                                ? "Unavailable"
                                : String(evidence?.endpoints ?? 0);
                    return (
                        <div key={policy.providerTag} className="grid gap-[7px] border-b border-[color:var(--line-soft)] pb-[10px] last:border-b-0 last:pb-0 md:grid-cols-[minmax(0,1fr)_110px_150px_minmax(220px,300px)] md:items-center">
                            <span className="grid gap-[2px] text-[12px] text-[color:var(--ink)]">
                                <span>{policy.displayName}</span>
                                <span className={`${NOTE_CLASS} font-mono text-[9px]`}>{policy.providerTag}</span>
                            </span>
                            <span className={`${NOTE_CLASS} text-[11px]`}>
                                <span className="mr-[5px] md:hidden">Endpoints:</span>{evidenceLabel}
                            </span>
                            <span className={`${NOTE_CLASS} text-[11px]`}>
                                <span className="mr-[5px] md:hidden">Last observed:</span>
                                {ignored
                                    ? "Hidden by policy"
                                    : lastObserved && !Number.isNaN(lastObserved.getTime())
                                        ? `${dayLabelFormatter.format(lastObserved)} ${timeLabelFormatter.format(lastObserved)}`
                                        : evidenceState === "ready" ? "Not observed" : "Unavailable"}
                            </span>
                            <div aria-busy={state.pending || undefined} className="grid gap-[3px]">
                                <Select
                                    ariaLabel={`${policy.displayName} disposition`}
                                    value={policy.disposition}
                                    onChange={(value) => onDispositionChange(policy.providerTag, value)}
                                    options={AI_SERVICE_DISPOSITIONS}
                                    disabled={!canWrite || state.pending}
                                />
                                {state.pending ? <span className={NOTE_CLASS}>Saving...</span> : null}
                                {state.error ? <span className="text-[11px] text-red-400">{state.error}</span> : null}
                                {state.note ? <span className={`${NOTE_CLASS} text-[11px]`}>{state.note}</span> : null}
                            </div>
                        </div>
                    );
                }) : null}
            </div>
            <ConfirmDialog
                open={Boolean(pendingIgnore)}
                destructive
                analyticsId={DIALOG.AI_SERVICE_IGNORE}
                eyebrow="Ignore AI service"
                title={`Ignore ${ignoreTarget?.displayName ?? "this service"}?`}
                body={`Ignoring applies to everyone in your organization and is retroactive: existing ${ignoreTarget?.displayName ?? "provider"} observations disappear from AI service views and analytics for every user, not just new activity. The underlying evidence is retained, and setting the service back to sanctioned or unsanctioned restores it.`}
                confirmLabel="Ignore service"
                onConfirm={() => {
                    const target = pendingIgnore;
                    setPendingIgnore(null);
                    if (target) save(target.providerTag, target.disposition);
                }}
                onCancel={() => setPendingIgnore(null)}
            />
        </SectionCard>
    );
}

export function AiProvidersSection({ canWrite }) {
    const [policyRefreshKey, setPolicyRefreshKey] = useState(0);
    const refreshPolicy = useCallback(() => {
        setPolicyRefreshKey((value) => value + 1);
    }, []);

    return (
        <SectionCard
            id="settings-ai-providers"
            eyebrow="Shadow AI"
            title="AI Providers"
            description="Define which provider hostnames count as AI activity and how your organization treats every recognized provider. Custom providers are unsanctioned until you approve them."
            collapsible
            defaultCollapsed
        >
            {canWrite ? (
                <CustomProvidersSection embedded onSaved={refreshPolicy} />
            ) : null}
            <AiServicesSection
                canWrite={canWrite}
                embedded
                refreshKey={policyRefreshKey}
            />
        </SectionCard>
    );
}

export default function SettingsPage({ viewDef }) {
    // Each section is gated on the permission its writes require — not a
    // role name (custom roles arrive in RBAC v2; name-gating would
    // break). Tenant-config sections (PUT /v1/tenant/configuration) need
    // tenant_config.write. PasswordSection / ClearLocalData are per-user,
    // always shown. Gates fail open on a pre-RBAC tenant, preserving prior UI.
    const caps = useMyPermissions();
    const canReadTenantConfig = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_READ);
    const canWriteTenantConfig = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
    // These two key-management RPC families still use the transitional
    // require_admin() shim, whose exact ABAC gate is tenant.users.manage.
    // Keep them separate from tenant-configuration writes so a custom role
    // never sees controls the backend will categorically reject.
    const canManageAdminIntegrations = hasPermission(caps, PERMISSIONS.USERS_MANAGE);
    return (
        <>
            <AdminViewHeader title={viewDef?.label ?? "Settings"} />
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto py-[12px]">
                <div className="grid gap-[14px]">
                    {/* The signed-in user's own scope (informational). Renders
                        nothing on a pre-RBAC tenant, so it's safe to keep at
                        the top of the always-visible content. */}
                    <AccessSummary />
                    {canWriteTenantConfig ? (
                        <>
                            <EndpointConfigurationSection />
                        </>
                    ) : null}
                    {canManageAdminIntegrations ? (
                        <>
                            <AnthropicApiKeySection showPermissionUnavailable />
                            <PerplexityAuditLogSection />
                        </>
                    ) : null}
                    {/* AI service dispositions steer the AI-service analytics
                        views; read access renders the card, write access is what
                        enables the dropdowns. */}
                    {canReadTenantConfig ? (
                        <AiProvidersSection canWrite={canWriteTenantConfig} />
                    ) : null}
                    <PasswordSection />
                    <ClearLocalDataSection />
                </div>
            </div>
        </>
    );
}
